import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles } from '../../../button/button.component';
import { combineLatest, forkJoin, Observable, of, Subscription, tap, throwError } from 'rxjs';
import { ToastService } from '../../../shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { DateAgoPipe } from '../../../shared/pipes/date-ago.pipe';
import { PolicyMisconfigurationPollingContext } from './policy-misconfiguration-polling.context';
import { PolicyMisconfigurationResource, TeamsPoliciesUntyped } from './policy-misconfiguration.resource';
import {
  AccountType,
  CurrentUser,
  JobState,
  JobStatus,
  MicrosoftPolicyConfiguration,
  MicrosoftPolicyMisconfigurationReport,
  TeamsVoicePolicies,
  ZpmPrivilege,
} from '../../../shared/models/generated/ziroModels';
import moment from 'moment/moment';
import { forOwn } from 'lodash';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { MicrosoftVoicePoliciesResource } from '../../../helpdesk/shared/resources/microsoft-voice-policies.resource';
import { Microsoft360ViewResource } from '../../../shared/resources/microsoft-360-view.resource';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { DatatableComponent } from '../../../shared/tables/datatable/datatable.component';
import { DatatableFieldTypes, DatatableSelectField } from '../../../shared/tables/datatable/datatable-field.models';
import { ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { DatatableFilterService } from '../../../shared/tables/datatable/datatable-filter.service';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { FilterSelectOption } from '../../../shared/tables/datatable/filter-select/filter-select.component';
import { catchError, finalize, map, mergeMap, take } from 'rxjs/operators';
import {
  DatatableColumn,
  DatatableOnRowFieldChange,
  DatatableRow,
} from '../../../shared/tables/datatable/datatable.models';
import {
  MicrosoftCachedOptionsWithTenantId,
  MsTeamsCachedOptionsResource,
} from '../../../admin/resources/ms-teams-cached-options.resource';
import { DatatableMultiSelectFilterComponent } from '../../../shared/tables/datatable/datatable-filters/datatable-multi-select-filter/datatable-multi-select-filter.component';
import { ActivatedRoute } from '@angular/router';
import { DatatableSwitchFilterComponent } from '../../../shared/tables/datatable/datatable-filters/datatable-switch-filter/datatable-switch-filter.component';
import { AuthenticationContext } from '../../../shared/contexts/authentication.context';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';

interface PolicyMisconfigurationReportData extends DatatableRow {
  tenantId: number;
  accountType: string;
  userPrincipalName: string;
  enterpriseVoiceEnabled: string;
  displayName: string;
  lineUri: string;
  dialPlanName: string;
  onlineVoiceRoutingPolicy: string;
  tenantDialPlan: string;
  emergencyCallingPolicy: string;
  emergencyCallRoutingPolicy: string;
  callingPolicy: string;
  ipPhonePolicy: string;
  teamsCallHoldPolicy: string;
  teamsCallParkPolicy: string;
  onlineVoicemailPolicy: string;
  callingLineIdentity: string;
  sharedCallRoutingPolicy: string;
  enterpriseVoiceRequired?: boolean;
  ignored?: boolean;
}

@Component({
  selector: 'ziro-microsoft-policy-misconfiguration-report',
  templateUrl: './policy-misconfiguration-report.component.html',
  styleUrls: ['../../reporting.scss'],
  providers: [
    PolicyMisconfigurationPollingContext,
    PolicyMisconfigurationResource,
    MicrosoftVoicePoliciesResource,
    Microsoft360ViewResource,
  ],
  standalone: false,
})
export class PolicyMisconfigurationReportComponent implements OnInit, OnDestroy {
  private _translateService = inject(TranslateService);
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _toastService = inject(ToastService);
  private _dateAgoPipe = inject(DateAgoPipe);
  private _policyMisconfigurationPollingContext = inject(PolicyMisconfigurationPollingContext);
  private _policyMisconfigurationResource = inject(PolicyMisconfigurationResource);
  private _microsoftVoicePoliciesResource = inject(MicrosoftVoicePoliciesResource);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _changeDetectRef = inject(ChangeDetectorRef);
  private _datatableFilterService = inject(DatatableFilterService);
  private _ziroModalService = inject(ZiroModalService);
  private _msTeamCachedOptionsResource = inject(MsTeamsCachedOptionsResource);
  private _route = inject(ActivatedRoute);
  private _authenticationContext = inject(AuthenticationContext);

  @ViewChild(DatatableComponent) datatableComponent: DatatableComponent<PolicyMisconfigurationReportData>;
  @ViewChild('dialPlanGroupFilter') dialPlanGroupFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('showIgnoredUsersFilter') showIgnoredUsersFilter: DatatableSwitchFilterComponent;

  buttonStyles = ButtonStyles;
  buttonSizes = ButtonSizes;
  ziroIcons = ZiroIcons;
  isLoading = true;
  tableDataAutomation = 'policy-misconfiguration-datatable';
  dialPlanGroupOptions: string[] = [];
  misconfiguredPoliciesOptions: FilterSelectOption[] = [];
  misconfiguredPoliciesMatchFnBound: (filter: string[], row: PolicyMisconfigurationReportData) => boolean;
  dialPlanGroupMatchFnBound: (filter: string[], row: PolicyMisconfigurationReportData) => boolean;
  columns: DatatableColumn<PolicyMisconfigurationReportData>[] = [
    {
      name: 'userPrincipalName',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.upn.label',
    },
    {
      name: 'displayName',
      label: 'tkey;reports.microsoft.policy_misconfig.table.display_name.label',
    },
    {
      name: 'enterpriseVoiceEnabled',
      columnCharacterWidth: 20,
      label: 'tkey;reports.microsoft.policy_misconfig.table.enterprise_voice.label',
    },
    {
      name: 'lineUri',
      columnCharacterWidth: 20,
      label: 'tkey;reports.microsoft.policy_misconfig.table.number.label',
    },
    {
      name: 'dialPlanName',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.dial_plan_group.label',
    },
    {
      name: 'onlineVoiceRoutingPolicy',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.voice_routing.label',
    },
    {
      name: 'tenantDialPlan',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.dial_plan.label',
    },
    {
      name: 'emergencyCallingPolicy',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.emergency_calling.label',
    },
    {
      name: 'emergencyCallRoutingPolicy',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.emergency_call_routing.label',
    },
    {
      name: 'callingPolicy',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.calling.label',
    },
    {
      name: 'ipPhonePolicy',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.ip_phone.label',
    },
    {
      name: 'teamsCallHoldPolicy',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.call_hold.label',
    },
    {
      name: 'teamsCallParkPolicy',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.call_park.label',
    },
    {
      name: 'onlineVoicemailPolicy',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.voicemail.label',
    },
    {
      name: 'callingLineIdentity',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.caller_id.label',
    },
    {
      name: 'sharedCallRoutingPolicy',
      columnCharacterWidth: 30,
      label: 'tkey;reports.microsoft.policy_misconfig.table.shared_calling.label',
    },
  ];
  data: PolicyMisconfigurationReportData[] = [];

  private _pollingSubscription: Subscription;
  private _subscriptions = new Subscription();
  private _isFixingMisconfiguredPoliciesInProgress = false;
  private _isApplyingDefaultsInProgress = false;
  private _misconfiguredUsers = new Map<string, Map<string, string>>();
  private _misconfigPolicyCountMap = new Map<string, number>();
  private _msCachedOptions: MicrosoftCachedOptionsWithTenantId;
  private _currentUser: CurrentUser;
  private _allMisconfiguredPoliciesOptions: FilterSelectOption[] = [];
  private _tenantId: number;

  private readonly _refreshButtonId = 'policy-misconfiguration-refresh';
  private readonly _applyDefaultsButtonId = 'apply-defaults';
  private readonly _fixMisconfiguredPoliciesButtonId = 'fix-misconfigured-policies';
  private readonly _ignoreUnignoreUsersButtonId = 'ignore-unignored-users';

  private readonly _ignoreUnignoreUsersButtonDataAutomation = 'policy-misconfiguration-ignore-unignore-users-button';
  private readonly _applyDefaultsButtonDataAutomation = 'policy-misconfiguration-apply-defaults-button';
  private readonly _fixMisconfiguredPoliciesButtonDataAutomation = 'policy-misconfiguration-fix-button';

  private _bottomNavButtons = [
    {
      id: this._refreshButtonId,
      dataAutomation: 'policy-misconfiguration-refresh',
      label: 'tkey;reports.microsoft.policy_misconfig.bottom_nav.refresh',
      icon: this.ziroIcons.REFRESH,
      buttonClass: this.buttonStyles.DEFAULT,
      cb: () => {
        this._refresh(true);
      },
    },
  ];

  get allSelectedRowsAreIgnored(): boolean {
    return this.data
      .filter((row: PolicyMisconfigurationReportData) => row.isSelectedInTable)
      .every((row) => row.ignored);
  }

  ngOnInit(): void {
    this._tenantId = Number(this._route.snapshot.paramMap.get('tenantId'));
    this._initBreadcrumbs();
    this.dialPlanGroupMatchFnBound = this.dialPlanGroupMatchFn.bind(this);
    this.misconfiguredPoliciesMatchFnBound = this.misconfiguredPoliciesMatchFn.bind(this);
    const sub = combineLatest([
      this._msTeamCachedOptionsResource.getByTenantId(this._tenantId),
      this._authenticationContext.state$,
    ])
      .pipe(
        map(([msCachedOptions, currentUser]) => {
          this._msCachedOptions = msCachedOptions;
          this._currentUser = currentUser;
          this._initPolling();
          this._listenToPolling();
        }),
      )
      .subscribe();
    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._allMisconfiguredPoliciesOptions = [];

    this._pollingSubscription.unsubscribe();
    this._subscriptions.unsubscribe();
  }

  dialPlanGroupMatchFn(filter: string[], row: PolicyMisconfigurationReportData): boolean {
    return this._datatableFilterService.filterMultiSelectValues(filter, row.dialPlanName);
  }

  misconfiguredPoliciesMatchFn(filter: string[], row: PolicyMisconfigurationReportData): boolean {
    if (!filter.length) {
      return true;
    }

    if (filter.length > 0) {
      return filter.some((filterVal) => {
        const key = filterVal as keyof PolicyMisconfigurationReportData;
        return this._isPolicyMisconfigured(row.fields[key] as DatatableSelectField);
      });
    }
  }

  showIgnoredUsersMatchFn = (showIgnored: boolean, row: PolicyMisconfigurationReportData): boolean =>
    showIgnored || !row.ignored;

  onRowFieldChange(change: DatatableOnRowFieldChange<PolicyMisconfigurationReportData>): void {
    const upn = change.row.userPrincipalName;
    this._updateMisconfiguredUsers(change, upn);

    this.data = this.data.map((row: PolicyMisconfigurationReportData) => {
      if (row.userPrincipalName === upn) {
        const updatedFields: { [key: string]: DatatableSelectField } = {};
        forOwn(row.fields, (field: DatatableSelectField, key: string) => {
          updatedFields[key] = {
            ...field,
          };
        });
        return {
          ...row,
          [change.columnId]: change.newValue as string,
          fields: {
            ...updatedFields,
          },
        };
      }
      return row;
    });

    this._updateBottomNav();
    this._changeDetectRef.detectChanges();
  }

  onSearchResultsChanged(): void {
    this._updateFilterCounts();
  }

  onRowSelected(): void {
    this._updateBottomNav();
    this._changeDetectRef.detectChanges();
  }

  private _updateMisconfiguredUsers(
    change: DatatableOnRowFieldChange<PolicyMisconfigurationReportData>,
    upn: string,
  ): void {
    const isWarningState = this.datatableComponent.isWarningPresentOnCell(change.row, change.columnId);
    if (isWarningState) {
      const policyMap = this._misconfiguredUsers.get(upn);
      policyMap.delete(change.columnId);
      if (policyMap.size === 0) {
        this._misconfiguredUsers.delete(upn);
      } else {
        this._misconfiguredUsers.set(upn, policyMap);
      }
    } else {
      let policyMap = this._misconfiguredUsers.get(upn);
      if (!policyMap) {
        policyMap = new Map<string, string>();
        this._misconfiguredUsers.set(upn, policyMap);
      }
      policyMap.set(change.columnId, change.newValue);
      this._misconfiguredUsers.set(upn, policyMap);
    }
  }

  private _initPolling(): void {
    if (!this._policyMisconfigurationPollingContext.getIsPolling()) {
      this._policyMisconfigurationPollingContext.startPolling();
    }
  }

  private _listenToPolling(isRefreshed = false): void {
    this._pollingSubscription = this._policyMisconfigurationPollingContext.state$.subscribe((state: JobStatus) => {
      if (state.jobState === JobState.IDLE && state.lastRunTime === null) {
        this._refresh();
      } else if (state.jobState === JobState.IDLE && state.lastRunTime !== null) {
        const secondsAgo = moment().diff(state.lastRunTime, 'seconds');
        if (secondsAgo > 60) {
          this._initBottomNav(state);
          this._refresh();
        } else {
          this._initBottomNav(state);

          this._initTableRows();
          if (isRefreshed) {
            this._toastService.push(
              ToastTypes.SUCCESS,
              ZiroIcons.SAVE_SUCCESS,
              'tkey;reports.microsoft.policy_misconfig.toast.refresh.title',
              'tkey;reports.microsoft.policy_misconfig.toast.refresh.content',
            );
          }
        }
        this._bottomNavService.setButtonPendingState(this._refreshButtonId, false);
      }
    });
  }

  private _initBottomNav(state: JobStatus): void {
    this._updateBottomNav();

    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: `
              <i class="${ZiroIcons.TIMESTAMP}"></i>
              ${this._translateService.instant(
                'tkey;reports.microsoft.policy_misconfig.bottom_nav.last_refreshed',
              )} <strong>${this._dateAgoPipe.transform(state.lastRunTime)}</strong>
            `,
      }),
    );
  }

  private _initTableRows(): void {
    this._policyMisconfigurationPollingContext.stopPolling();
    this._pollingSubscription.unsubscribe();

    this._policyMisconfigurationResource
      .get(this._tenantId.toString())
      .subscribe((data: MicrosoftPolicyMisconfigurationReport[]) => {
        const invalidRows: MicrosoftPolicyMisconfigurationReport[] = data.filter(
          (row: MicrosoftPolicyMisconfigurationReport) => {
            let isValid = true;

            forOwn(row, (value: string | MicrosoftPolicyConfiguration) => {
              if (value && typeof value === 'object') {
                const typedValue = value as MicrosoftPolicyConfiguration;
                if (!typedValue.valid) {
                  isValid = false;
                  return false;
                }
              }
            });

            const enterpriseVoiceRequiredButNotEnabled =
              row.enterpriseVoiceRequired === true && row.enterpriseVoiceEnabled === false;

            return !isValid || enterpriseVoiceRequiredButNotEnabled;
          },
        );

        this._buildData(invalidRows);
        this._buildFilterOptions(invalidRows);

        this.isLoading = false;
      });
  }

  private _buildFilterOptions(invalidRows: MicrosoftPolicyMisconfigurationReport[]): void {
    this.dialPlanGroupOptions = Array.from(new Set(invalidRows.map((invalidRow) => invalidRow.dialPlanName)));
    this._allMisconfiguredPoliciesOptions = Array.from(this._misconfigPolicyCountMap.keys())
      .map((policyName) => {
        const column = this.columns.find((col) => col.name === policyName);
        const policyLabel = column ? this._translateService.instant(column.label) : policyName;
        const count: number = this._misconfigPolicyCountMap.get(policyName) ?? 0;
        return {
          label: policyLabel,
          value: policyName,
          count: count,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    this.misconfiguredPoliciesOptions = [...this._allMisconfiguredPoliciesOptions];
  }

  private _refresh(refreshClicked = false): void {
    this._policyMisconfigurationPollingContext.stopPolling();
    this._pollingSubscription.unsubscribe();
    this._bottomNavService.setButtonPendingState(this._refreshButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._fixMisconfiguredPoliciesButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._applyDefaultsButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._ignoreUnignoreUsersButtonId, true);
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: null,
      }),
    );

    this.isLoading = true;

    this._policyMisconfigurationResource.refresh(this._tenantId.toString()).subscribe(() => {
      this._misconfiguredUsers = new Map<string, Map<string, string>>();

      this._initPolling();
      this._listenToPolling(refreshClicked);
    });
  }

  private _initBreadcrumbs(): void {
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;reports.microsoft.policy_misconfig.title' }]);
  }

  private _buildData(invalidRows: MicrosoftPolicyMisconfigurationReport[]): void {
    this._misconfigPolicyCountMap = new Map<string, number>();
    this.data = invalidRows.map((row: MicrosoftPolicyMisconfigurationReport) => {
      const fields: { [key: string]: DatatableSelectField } = {};
      const ignoredTooltip = row.ignored
        ? this._translateService.instant('tkey;reports.microsoft.policy_misconfig.table.ignored_user.tooltip')
        : undefined;

      forOwn(row, (value: MicrosoftPolicyConfiguration | boolean, key: string) => {
        if (key === 'enterpriseVoiceEnabled' && row.enterpriseVoiceRequired && value === false) {
          fields[key] = {
            type: DatatableFieldTypes.SELECT,
            value: 'false',
            options: [
              {
                label: this._translateService.instant(
                  'tkey;reports.microsoft.policy_misconfig.table.enterprise_voice.status.not_enabled',
                ),
                value: 'false',
                iconHtml: `<i class="icon ${this.ziroIcons.WARNING_TRIANGLE} me-1 text-warning"></i>`,
              },
              {
                label: this._translateService.instant(
                  'tkey;reports.microsoft.policy_misconfig.table.enterprise_voice.status.enabled',
                ),
                value: 'true',
              },
            ],
            showOptionsIcons: true,
            bindValue: 'value',
            rowId: key,
            showSuccessBorder: true,
            validation: [
              {
                validator: (val) => {
                  return val === 'false' ? ZiroFormsValidationState.WARNING : ZiroFormsValidationState.VALID;
                },
                message: () => '',
              },
            ],
            isDisabled: row.ignored,
          };
          if (!row.ignored) {
            this._addToPolicyCountMap(key);
          }
        } else if (value && typeof value === 'object') {
          const typedValue = value as MicrosoftPolicyConfiguration;
          if (!typedValue.valid) {
            const possibleOptions: string[] = typedValue.possibleOptions.filter(
              (option: string) => option !== value.defaultPolicy,
            );

            fields[key] = {
              type: DatatableFieldTypes.SELECT,
              value: typedValue.currentPolicy,
              options: [
                {
                  label: value.currentPolicy,
                  value: value.currentPolicy,
                  iconHtml: `<i class="icon ${this.ziroIcons.WARNING_TRIANGLE} me-1 text-warning"></i>`,
                },
                {
                  label: value.defaultPolicy,
                  value: value.defaultPolicy,
                  iconHtml: `<i class="icon ${this.ziroIcons.TOOLTIP} me-1"></i>`,
                  iconHtmlTooltip: this._translateService.instant(
                    'tkey;reports.microsoft.policy_misconfig.table.select.default_value',
                  ),
                },
                ...possibleOptions.map((option) => {
                  return {
                    label: option,
                    value: option,
                  };
                }),
              ],
              showOptionsIcons: true,
              bindValue: 'value',
              rowId: key,
              showSuccessBorder: true,
              validation: [
                {
                  validator: (val) => {
                    return value.currentPolicy === val
                      ? ZiroFormsValidationState.WARNING
                      : ZiroFormsValidationState.VALID;
                  },
                  message: () => '',
                },
              ],
              isDisabled: row.ignored,
            };
            if (!row.ignored) {
              this._addToPolicyCountMap(key);
            }
          }
        }
      });

      const fetchSharedCallingNumber = !row.lineUri;
      const lineUriTooltip =
        fetchSharedCallingNumber && this._msCachedOptions
          ? this._msCachedOptions.sharedCallingRoutingPolicies?.find(
              (policy) => policy.policyName === row.sharedCallRoutingPolicy.currentPolicy,
            )?.resourceAccountLineUri
          : '';

      return {
        tenantId: row.tenantId,
        userPrincipalName: row.upn,
        displayName: row.displayName,
        lineUri: row.lineUri
          ? this._telephoneNumberFilter.transform(row.lineUri)
          : this._translateService.instant(
              'tkey;reports.microsoft.policy_misconfig.table.number.shared_calling_dpg.text',
            ),
        dialPlanName: row.dialPlanName,
        onlineVoiceRoutingPolicy: row.onlineVoiceRoutingPolicy?.currentPolicy ?? '',
        tenantDialPlan: row.tenantDialPlan?.currentPolicy ?? '',
        emergencyCallingPolicy: row.emergencyCallingPolicy?.currentPolicy ?? '',
        emergencyCallRoutingPolicy: row.emergencyCallRoutingPolicy?.currentPolicy ?? '',
        callingPolicy: row.callingPolicy?.currentPolicy ?? '',
        ipPhonePolicy: row.ipPhonePolicy?.currentPolicy ?? '',
        teamsCallHoldPolicy: row.teamsCallHoldPolicy?.currentPolicy ?? '',
        teamsCallParkPolicy: row.teamsCallParkPolicy?.currentPolicy ?? '',
        onlineVoicemailPolicy: row.onlineVoicemailPolicy?.currentPolicy ?? '',
        callingLineIdentity: row.callingLineIdentity?.currentPolicy ?? '',
        sharedCallRoutingPolicy: row.sharedCallRoutingPolicy?.currentPolicy ?? '',
        accountType: row.accountType ?? '',
        enterpriseVoiceEnabled: row.enterpriseVoiceEnabled
          ? this._translateService.instant(
              'tkey;reports.microsoft.policy_misconfig.table.enterprise_voice.status.enabled',
            )
          : this._translateService.instant(
              'tkey;reports.microsoft.policy_misconfig.table.enterprise_voice.status.not_enabled',
            ),
        enterpriseVoiceRequired: row.enterpriseVoiceRequired,
        isTableRowSelectable: true,
        cssClass: row.ignored ? 'datatable__row--ignored' : undefined,
        rowTooltip: ignoredTooltip,
        fields: fields,
        hardcodedTooltips: {
          lineUri: fetchSharedCallingNumber ? this._telephoneNumberFilter.transform(lineUriTooltip) : '',
          dialPlanName: row.dialPlanName.length > 30 ? row.dialPlanName : '',
        },
        ignored: row.ignored,
      };
    });
  }

  private _addToPolicyCountMap(policy: string): void {
    const currentCount = this._misconfigPolicyCountMap.get(policy) ?? 0;
    this._misconfigPolicyCountMap.set(policy, currentCount + 1);
  }

  private _onFixMisconfiguredPoliciesClicked(): void {
    const modifiedUsers = Array.from(this._misconfiguredUsers.keys());
    const modifiedPolicies = this._getCountOfModifiedValidPolicies();
    const options = {
      windowClass: 'fix-misconfigured-policies-modal',
      modalViewProperties: {
        title: 'tkey;reports.microsoft.policy_misconfig.table.misconfigured_policies.modal.title',
        icon: ZiroIcons.FIX_IT,
        iconClass: 'text-info',
        modalBodyIconHeaderClass: 'animated bounceIn lead text-center',
        promptBody: this._translateService.instant(
          modifiedPolicies > 1
            ? 'tkey;reports.microsoft.policy_misconfig.table.misconfigured_policies.multiple_policies.modal.prompt'
            : 'tkey;reports.microsoft.policy_misconfig.table.misconfigured_policies.single_policy.modal.prompt',
          {
            count: modifiedPolicies,
            users: modifiedUsers.join(', '),
          },
        ),
        promptBodyClass: 'word-break-keep-all',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;reports.microsoft.policy_misconfig.table.misconfigured_policies.modal.confirm.button.label',
            buttonClass: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'confirmation-modal-confirm-button',
            isPending: this._isFixingMisconfiguredPoliciesInProgress,
            cb: () => this._onFixMisconfiguredPoliciesConfirm(),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _onFixMisconfiguredPoliciesConfirm(): Observable<void[]> {
    const upnsToUpdate = Array.from(this._misconfiguredUsers.keys());
    const requests = upnsToUpdate.map((upn) => {
      const upnRow = this.data.find((row) => row.userPrincipalName === upn);
      const isResourceAccount = upnRow.accountType === AccountType.RESOURCE_ACCOUNT;
      const updatePolicies = this._misconfiguredUsers.get(upn);
      const hasEnterpriseVoiceChange = updatePolicies.has('enterpriseVoiceEnabled');
      const onlyEnterpriseVoiceChanged = hasEnterpriseVoiceChange && updatePolicies.size === 1;
      const enterpriseVoiceValue = updatePolicies.get('enterpriseVoiceEnabled') === 'true';

      const enterpriseVoiceRequest$ = hasEnterpriseVoiceChange
        ? this._policyMisconfigurationResource.updateEnterpriseVoice(Number(this._tenantId), upn, enterpriseVoiceValue)
        : of(null);

      if (onlyEnterpriseVoiceChanged) {
        return enterpriseVoiceRequest$.pipe(
          tap(() => {
            this._toastService.push(
              ToastTypes.SUCCESS,
              ZiroIcons.SAVE_SUCCESS,
              'tkey;shared.toast.save.success.title',
              'tkey;reports.microsoft.policy_misconfig.toast.success',
            );
          }),
          catchError((error) => {
            if (error.status === 422) {
              this._toastService.push(
                ToastTypes.ERROR,
                ZiroIcons.WARNING_TRIANGLE,
                'tkey;reports.microsoft.policy_misconfig.toast.error.title',
                this._translateService.instant('tkey;reports.microsoft.policy_misconfig.toast.error.message', {
                  upn: upn,
                  errorMessage: error.error?.description,
                }),
              );
              return of(null);
            }
            return throwError(() => error);
          }),
        );
      }

      return enterpriseVoiceRequest$.pipe(
        mergeMap(() =>
          this._microsoftVoicePoliciesResource.get(this._tenantId, upn, isResourceAccount).pipe(
            map((data: TeamsVoicePolicies) => {
              const teamsVoicePolicies: TeamsPoliciesUntyped = { ...data };
              Array.from(updatePolicies.keys()).forEach((key) => {
                if (key !== 'enterpriseVoiceEnabled') {
                  teamsVoicePolicies[key] = updatePolicies.get(key);
                }
              });
              return { upn, teamsVoicePolicies, isResourceAccount };
            }),
            mergeMap(({ upn, teamsVoicePolicies, isResourceAccount }) =>
              this._microsoftVoicePoliciesResource.put(this._tenantId, upn, teamsVoicePolicies, isResourceAccount).pipe(
                tap(() => {
                  this._toastService.push(
                    ToastTypes.SUCCESS,
                    ZiroIcons.SAVE_SUCCESS,
                    'tkey;shared.toast.save.success.title',
                    'tkey;reports.microsoft.policy_misconfig.toast.success',
                  );
                }),
              ),
            ),
            catchError((error) => {
              if (error.status === 422) {
                this._toastService.push(
                  ToastTypes.ERROR,
                  ZiroIcons.WARNING_TRIANGLE,
                  'tkey;reports.microsoft.policy_misconfig.toast.error.title',
                  this._translateService.instant('tkey;reports.microsoft.policy_misconfig.toast.error.message', {
                    upn: upn,
                    errorMessage: error.error?.description,
                  }),
                );
                return of(null);
              }
              return throwError(() => error);
            }),
          ),
        ),
      );
    });

    this._bottomNavService.setButtonPendingState(this._refreshButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._fixMisconfiguredPoliciesButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._applyDefaultsButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._ignoreUnignoreUsersButtonId, true);

    return forkJoin([...requests]).pipe(
      finalize(() => {
        this._refresh(true);
      }),
    );
  }

  private _getFixItLabel(): string {
    const count = this._getCountOfModifiedValidPolicies();
    if (count < 1) {
      return 'tkey;reports.microsoft.policy_misconfig.bottom_nav.fix_it.button.disabled.label';
    }

    if (count === 1) {
      return 'tkey;reports.microsoft.policy_misconfig.bottom_nav.fix_it.single_policy.button.label';
    }

    return 'tkey;reports.microsoft.policy_misconfig.bottom_nav.fix_it.multiple_policy.button.label';
  }

  private _updateBottomNav(): void {
    const selectedRowsCount = this._getSelectedRowsCount();
    const validPoliciesCount = this._getCountOfModifiedValidPolicies();
    const allSelectedRowsAreIgnored = selectedRowsCount > 0 ? this.allSelectedRowsAreIgnored : false;
    const hasIgnoredSelected = this._hasIgnoredRowSelected();
    const applyDefaultsDisabled = selectedRowsCount < 1 || hasIgnoredSelected;
    const applyDefaultsTooltipKey = hasIgnoredSelected
      ? 'tkey;reports.microsoft.policy_misconfig.bottom_nav.apply_defaults.rows.disabled.button.tooltip'
      : 'tkey;reports.microsoft.policy_misconfig.bottom_nav.apply_defaults.button.disabled.tooltip';

    const buttons: BottomNavButton[] = [
      ...this._bottomNavButtons,
      {
        id: this._ignoreUnignoreUsersButtonId,
        dataAutomation: this._ignoreUnignoreUsersButtonDataAutomation,
        label: !selectedRowsCount
          ? 'tkey;reports.microsoft.policy_misconfig.bottom_nav.ignore_unignore_users.button.label'
          : allSelectedRowsAreIgnored
            ? this._translateService.instant(
                'tkey;reports.microsoft.policy_misconfig.bottom_nav.unignore_users.button.label',
                {
                  rowNumbers: selectedRowsCount,
                },
              )
            : this._translateService.instant(
                'tkey;reports.microsoft.policy_misconfig.bottom_nav.ignore_users.button.label',
                {
                  rowNumbers: selectedRowsCount,
                },
              ),

        icon: !selectedRowsCount ? undefined : allSelectedRowsAreIgnored ? ZiroIcons.UNIGNORE : ZiroIcons.IGNORE,
        buttonClass: ButtonStyles.SECONDARY,
        state: {
          buttonDisableState: {
            disabled: selectedRowsCount < 1,
            tooltipKey:
              'tkey;reports.microsoft.policy_misconfig.bottom_nav.ignore_unignore_users.button.disabled.tooltip',
          },
          tooltipVisible: selectedRowsCount < 1,
        },
        cb: () => this._ignoreUnignoreUsers(),
      },
    ];
    if (
      this._authenticationContext.userHasPrivilege(
        this._currentUser,
        ZpmPrivilege.TEAMS_USER_MANAGEMENT,
        this._tenantId,
      )
    ) {
      buttons.push(
        {
          id: this._applyDefaultsButtonId,
          dataAutomation: this._applyDefaultsButtonDataAutomation,
          label: 'tkey;reports.microsoft.policy_misconfig.bottom_nav.apply_defaults.button.label',
          icon: this.ziroIcons.FIX_IT,
          buttonClass: ButtonStyles.SECONDARY,
          state: {
            buttonDisableState: {
              disabled: applyDefaultsDisabled,
              tooltipKey: applyDefaultsTooltipKey,
            },
            tooltipVisible: applyDefaultsDisabled,
          },
          cb: () => this._onApplyDefaultsClicked(),
        },
        {
          id: this._fixMisconfiguredPoliciesButtonId,
          dataAutomation: this._fixMisconfiguredPoliciesButtonDataAutomation,
          label: this._getFixItLabel(),
          labelParam: validPoliciesCount,
          icon: this.ziroIcons.FIX_IT,
          buttonClass: this.buttonStyles.PRIMARY,
          state: {
            buttonDisableState: {
              disabled: validPoliciesCount < 1,
              tooltipKey: 'tkey;reports.microsoft.policy_misconfig.bottom_nav.fix_it.button.disabled.tooltip',
            },
            tooltipVisible: validPoliciesCount < 1,
          },
          cb: () => this._onFixMisconfiguredPoliciesClicked(),
        },
      );
    }
    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(buttons));
  }

  private _getSelectedRowsCount(): number {
    return this.data.filter((row) => row.isSelectedInTable).length;
  }

  private _hasIgnoredRowSelected(): boolean {
    return this.data.some((row) => row.isSelectedInTable && row.ignored);
  }

  private _getDefaultPolicyFromField(field: DatatableSelectField): string | undefined {
    if (!field?.options?.length) {
      return undefined;
    }
    const defaultOption = field.options.find(
      (opt: FilterSelectOption) => opt && typeof opt === 'object' && opt.iconHtmlTooltip,
    );
    return defaultOption?.value ?? (field.options[1] as { value?: string })?.value;
  }

  private _getDefaultPoliciesMapForSelectedRows(): Map<string, Map<string, string>> {
    const result = new Map<string, Map<string, string>>();
    const selectedRows = this.data.filter((row) => row.isSelectedInTable);

    for (const row of selectedRows) {
      const policyMap = new Map<string, string>();

      forOwn(row.fields || {}, (field: DatatableSelectField, key: string) => {
        if (this._isPolicyMisconfigured(field)) {
          const defaultPolicy = this._getDefaultPolicyFromField(field);
          if (defaultPolicy) {
            policyMap.set(key, defaultPolicy);
          }
        }
      });

      if (
        row.enterpriseVoiceRequired &&
        row.fields?.['enterpriseVoiceEnabled'] &&
        this._isPolicyMisconfigured(row.fields['enterpriseVoiceEnabled'] as DatatableSelectField)
      ) {
        policyMap.set('enterpriseVoiceEnabled', 'true');
      }

      if (policyMap.size > 0) {
        result.set(row.userPrincipalName, policyMap);
      }
    }

    return result;
  }

  private _onApplyDefaultsClicked(): void {
    const defaultPoliciesMap = this._getDefaultPoliciesMapForSelectedRows();
    const selectedUpns = Array.from(defaultPoliciesMap.keys());
    const userCount = selectedUpns.length;

    if (userCount < 1) {
      return;
    }

    const options = {
      windowClass: 'apply-defaults-policies-modal',
      modalViewProperties: {
        title: 'tkey;reports.microsoft.policy_misconfig.table.apply_defaults.modal.title',
        icon: ZiroIcons.FIX_IT,
        iconClass: 'text-info',
        modalBodyIconHeaderClass: 'animated bounceIn lead text-center',
        promptBody: this._translateService.instant(
          'tkey;reports.microsoft.policy_misconfig.table.apply_defaults.modal.prompt',
          {
            count: userCount,
            users: selectedUpns.join(', '),
          },
        ),
        promptBodyClass: 'word-break-keep-all',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;reports.microsoft.policy_misconfig.table.apply_defaults.modal.confirm.button.label',
            buttonClass: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'confirmation-modal-confirm-button',
            isPending: this._isApplyingDefaultsInProgress,
            cb: () => this._onApplyDefaultsConfirm(),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _onApplyDefaultsConfirm(): Observable<void[]> {
    const defaultPoliciesMap = this._getDefaultPoliciesMapForSelectedRows();
    const upnsToUpdate = Array.from(defaultPoliciesMap.keys());

    const requests = upnsToUpdate.map((upn) => {
      const upnRow = this.data.find((row) => row.userPrincipalName === upn);
      const isResourceAccount = upnRow.accountType === AccountType.RESOURCE_ACCOUNT;
      const updatePolicies = defaultPoliciesMap.get(upn);
      const hasEnterpriseVoiceChange = updatePolicies.has('enterpriseVoiceEnabled');
      const onlyEnterpriseVoiceChanged = hasEnterpriseVoiceChange && updatePolicies.size === 1;
      const enterpriseVoiceValue = updatePolicies.get('enterpriseVoiceEnabled') === 'true';

      const enterpriseVoiceRequest$ = hasEnterpriseVoiceChange
        ? this._policyMisconfigurationResource.updateEnterpriseVoice(Number(this._tenantId), upn, enterpriseVoiceValue)
        : of(null);

      if (onlyEnterpriseVoiceChanged) {
        return enterpriseVoiceRequest$.pipe(
          tap(() => {
            this._toastService.push(
              ToastTypes.SUCCESS,
              ZiroIcons.SAVE_SUCCESS,
              'tkey;shared.toast.save.success.title',
              'tkey;reports.microsoft.policy_misconfig.toast.success',
            );
          }),
          catchError((error) => {
            if (error.status === 422) {
              this._toastService.push(
                ToastTypes.ERROR,
                ZiroIcons.WARNING_TRIANGLE,
                'tkey;reports.microsoft.policy_misconfig.toast.error.title',
                this._translateService.instant('tkey;reports.microsoft.policy_misconfig.toast.error.message', {
                  upn: upn,
                  errorMessage: error.error?.description,
                }),
              );
              return of(null);
            }
            return throwError(() => error);
          }),
        );
      }

      return enterpriseVoiceRequest$.pipe(
        mergeMap(() =>
          this._microsoftVoicePoliciesResource.get(this._tenantId, upn, isResourceAccount).pipe(
            map((data: TeamsVoicePolicies) => {
              const teamsVoicePolicies: TeamsPoliciesUntyped = { ...data };
              Array.from(updatePolicies.keys()).forEach((key) => {
                if (key !== 'enterpriseVoiceEnabled') {
                  teamsVoicePolicies[key] = updatePolicies.get(key);
                }
              });
              return { upn, teamsVoicePolicies, isResourceAccount };
            }),
            mergeMap(({ upn, teamsVoicePolicies, isResourceAccount }) =>
              this._microsoftVoicePoliciesResource.put(this._tenantId, upn, teamsVoicePolicies, isResourceAccount).pipe(
                tap(() => {
                  this._toastService.push(
                    ToastTypes.SUCCESS,
                    ZiroIcons.SAVE_SUCCESS,
                    'tkey;shared.toast.save.success.title',
                    'tkey;reports.microsoft.policy_misconfig.toast.success',
                  );
                }),
              ),
            ),
            catchError((error) => {
              if (error.status === 422) {
                this._toastService.push(
                  ToastTypes.ERROR,
                  ZiroIcons.WARNING_TRIANGLE,
                  'tkey;reports.microsoft.policy_misconfig.toast.error.title',
                  this._translateService.instant('tkey;reports.microsoft.policy_misconfig.toast.error.message', {
                    upn: upn,
                    errorMessage: error.error?.description,
                  }),
                );
                return of(null);
              }
              return throwError(() => error);
            }),
          ),
        ),
      );
    });

    this._isApplyingDefaultsInProgress = true;
    this._bottomNavService.setButtonPendingState(this._refreshButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._fixMisconfiguredPoliciesButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._applyDefaultsButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._ignoreUnignoreUsersButtonId, true);

    return forkJoin([...requests]).pipe(
      finalize(() => {
        this._isApplyingDefaultsInProgress = false;
        this._refresh(true);
      }),
    );
  }

  private _getCountOfModifiedValidPolicies(): number {
    let total = 0;
    for (const modifiedValidPolicyMap of this._misconfiguredUsers.values()) {
      total += modifiedValidPolicyMap.size;
    }
    return total;
  }

  private _isPolicyMisconfigured(field: DatatableSelectField): boolean {
    return field?.value && !!field.options && field.options.length > 1;
  }

  private _ignoreUnignoreUsers(): void {
    const selectedRows = this.data.filter((row) => row.isSelectedInTable);
    const allSelectedRowsAreIgnored = selectedRows.length > 0 && selectedRows.every((row) => row.ignored);

    this._bottomNavService.setButtonPendingState(this._refreshButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._fixMisconfiguredPoliciesButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._applyDefaultsButtonId, true);
    this._bottomNavService.setButtonDisabledState(this._ignoreUnignoreUsersButtonId, true);
    const currentIgnoredUpns = this.data.filter((row) => row.ignored).map((row) => row.userPrincipalName);

    let newIgnoredList: string[];
    if (allSelectedRowsAreIgnored) {
      newIgnoredList = currentIgnoredUpns.filter((upn) => !selectedRows.some((sel) => sel.userPrincipalName === upn));
    } else {
      const toAdd = selectedRows.filter((row) => !row.ignored).map((row) => row.userPrincipalName);
      newIgnoredList = [...currentIgnoredUpns, ...toAdd];
    }

    this._policyMisconfigurationResource
      .updateIgnoredUsers(this._tenantId.toString(), newIgnoredList)
      .pipe(
        take(1),
        tap(() => {
          this._toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.SAVE_SUCCESS,
            'tkey;shared.toast.save.success.title',
            allSelectedRowsAreIgnored
              ? 'tkey;reports.microsoft.policy_misconfig.toast.unignore.success'
              : 'tkey;reports.microsoft.policy_misconfig.toast.ignore.success',
          );
        }),
        finalize(() => this._refresh(false)),
      )
      .subscribe();
  }

  private _updateFilterCounts(): void {
    const policyCountMap = new Map(Array.from(this._misconfigPolicyCountMap.keys(), (key) => [key, 0]));

    const selectedDialPlanGroups = this.dialPlanGroupFilter?.selectedItems.map((item) =>
      typeof item === 'string' ? item : item.value,
    );
    const showIgnoredUsers = this.showIgnoredUsersFilter?.value === true;

    const filteredData = this.datatableComponent
      ? this.data.filter(
          (datum) =>
            (showIgnoredUsers || !datum.ignored) &&
            this.datatableComponent.matchesSearchBar(datum) &&
            this.dialPlanGroupMatchFn(selectedDialPlanGroups, datum),
        )
      : [...this.data];

    filteredData.forEach((row) => {
      Object.keys(row.fields || {}).forEach((fieldKey) => {
        const field = row.fields?.[fieldKey as keyof typeof row.fields] as DatatableSelectField;
        if (this._isPolicyMisconfigured(field)) {
          const currentCount = policyCountMap.get(fieldKey) || 0;
          policyCountMap.set(fieldKey, currentCount + 1);
        }
      });
    });

    this.misconfiguredPoliciesOptions = Array.from(policyCountMap.entries())
      .map(([policyKey, countVal]) => {
        const column = this.columns.find((col) => col.name === policyKey);
        const policyLabel = column ? this._translateService.instant(column.label) : policyKey;
        const count: number = countVal ?? 0;

        return {
          label: policyLabel,
          value: policyKey,
          count: count,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    this._changeDetectRef.detectChanges();
  }
}
